import { useState, useEffect } from 'react';
import { useAuth } from '../../../../../context/contexts';
import { supabase } from '../../../../../lib/clients/supabase';
import { toast } from 'sonner';
import './Profile.css';

const Profile = () => {
    const { currentUser, userProfile } = useAuth();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        username: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (userProfile) {
            setFormData({
                firstName: userProfile.first_name || '',
                lastName: userProfile.last_name || '',
                email: currentUser?.email || '',
                phone: userProfile.phone_number || '',
                username: userProfile.username || ''
            });
        }
    }, [userProfile, currentUser]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Update profile in Supabase
            const { error: profileError } = await supabase
                .from('profiles')
                .update({
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    phone_number: formData.phone,
                    username: formData.username,
                    updated_at: new Date()
                })
                .eq('id', currentUser.id);

            if (profileError) throw profileError;

            // If email has changed, update auth email
            if (formData.email !== currentUser.email) {
                const { error: emailError } = await supabase.auth.updateUser({
                    email: formData.email
                });

                if (emailError) throw emailError;
            }

            toast.success('Profile updated successfully');
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error(error.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='profile-container'>
            <form onSubmit={handleSubmit}>
                <div className='name-group'>
                    <div className='form-group'>
                        <label htmlFor='firstName'>Name</label>
                        <input
                            type='text'
                            id='firstName'
                            name='firstName'
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder='First name'
                        />
                    </div>
                    <div className='form-group'>
                        <input
                            type='text'
                            id='lastName'
                            name='lastName'
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder='Last name'
                        />
                    </div>
                </div>

                <div className='form-group'>
                    <label htmlFor='email'>Email address</label>
                    <div className='email-container'>
                        <input
                            type='email'
                            id='email'
                            name='email'
                            value={formData.email}
                            onChange={handleChange}
                            placeholder='email@example.com'
                        />
                        {userProfile?.email_verified && (
                            <span className='verified-badge'>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M8 0L10.2 2.8L13.6 3.2L13.2 6.6L16 8.8L13.2 11L13.6 14.4L10.2 14.8L8 17.6L5.8 14.8L2.4 14.4L2.8 11L0 8.8L2.8 6.6L2.4 3.2L5.8 2.8L8 0Z" fill="#4CAF50" />
                                    <path d="M7 9.4L5.6 8L4.9 8.7L7 10.8L11.1 6.7L10.4 6L7 9.4Z" fill="white" />
                                </svg>
                                VERIFIED {new Date(userProfile.email_verified_at).toLocaleDateString('en-US', { 
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                }).toUpperCase()}
                            </span>
                        )}
                    </div>
                </div>

                <div className='form-group'>
                    <label htmlFor='phone'>Phone number</label>
                    <div className='phone-container'>
                        <input
                            type='tel'
                            id='phone'
                            name='phone'
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder='+1 (555) 000-0000'
                        />
                    </div>
                </div>

                <div className='form-group'>
                    <label htmlFor='username'>Username</label>
                    <div className='username-container'>
                        <span className='prefix'>pmcardetailing.com/</span>
                        <input
                            type='text'
                            id='username'
                            name='username'
                            value={formData.username}
                            onChange={handleChange}
                            placeholder='username'
                        />
                        <button 
                            type='button' 
                            className='copy-button'
                            onClick={() => {
                                navigator.clipboard.writeText(`pmcardetailing.com/${formData.username}`);
                                toast.success('Username copied to clipboard');
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M2 2H10V10H2V2Z" stroke="currentColor" strokeWidth="1.5" />
                                <path d="M6 6H14V14H6V6Z" stroke="currentColor" strokeWidth="1.5" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className='form-actions'>
                    <button type='button' className='btn-cancel' onClick={() => setFormData({
                        firstName: userProfile?.first_name || '',
                        lastName: userProfile?.last_name || '',
                        email: currentUser?.email || '',
                        phone: userProfile?.phone_number || '',
                        username: userProfile?.username || ''
                    })}>
                        Cancel
                    </button>
                    <button type='submit' className='btn-save' disabled={loading}>
                        {loading ? 'Saving...' : 'Save changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Profile;